/// <amd-module name="ServiceHooks/Web/Admin/Components/ServiceHooksTabContent/ServiceHooksTabContent" />
import * as React from "react";
import { InputFilterOperator, InputValues } from "ServiceHooks/Web/Admin/Contracts/FormInput";
import { UpdateSubscripitonDiagnosticsParameters } from "ServiceHooks/Web/Admin/Contracts/Notifications";
import {
    Consumer,
    ConsumerAction,
    EventTypeDescriptor,
    Notification,
    NotificationsQuery,
    NotificationSummary,
    PublishersQuery,
    Subscription,
    SubscriptionInputScope,
    SubscriptionInputValuesQuery,
    SubscriptionsQuery,
    SubscriptionStatus
} from "ServiceHooks/Web/Admin/Contracts/ServiceHooks";
import * as Resources from "ServiceHooks/Web/Admin/Resources";
import { getServiceHooksClient, IServiceHooksRestClient } from "ServiceHooks/Web/Admin/RestClient/ServiceHooks";
import { ITfsPageData, ITfsPageService } from "Tfs/Platform/Page";
import { IObservableArray, IObservableObject, IObservableValue, ObservableArray, ObservableObject, ObservableValue } from "VSS/Core/Observable";
import { ICancelablePromise, makeCancelable } from "VSS/Core/Util/Promise";
import { format } from "VSS/Core/Util/String";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { Components, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { Dialog } from "VSSUI/Dialog";
import { TitleSize } from "VSSUI/Header";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { ServiceHooksDataContext } from "../../Context/ServiceHooksDataContext";
import { IServiceHooksDataContext } from "../../Context/ServiceHooksDataContext.Types";
import { Permissions } from "../../Contracts/Permissions";
import { NotificationDetailView } from "../NotificationDetailView/NotificationDetailView";
import { ServiceHookDetailView } from "../ServiceHookDetailView/ServiceHookDetailView";
import { ServiceHooksView } from "../ServiceHooksView/ServiceHooksView";
import { SubscriptionCreationPanel } from "../SubscriptionCreationPanel/SubscriptionCreationPanel";
import { ServiceHooksDefaultServiceData } from "./ServiceHooksTabContent.Types";

export class ServiceHooksTabContent extends VssComponent<IVssComponentProperties> {
    private readonly consumers: Consumer[] = [];
    private readonly consumersById: { [key: string]: Consumer } = {};
    private readonly consumerActions: ConsumerAction[] = [];
    private readonly consumerActionsById: { [key: string]: ConsumerAction } = {};
    private readonly eventTypes: EventTypeDescriptor[] = [];
    private readonly eventTypesById: { [key: string]: EventTypeDescriptor } = {};
    private readonly eventTypeIdsToPublisherTypeIds: { [key: string]: string } = {};

    private readonly notificationSummaries: IObservableObject<NotificationSummary> = new ObservableObject<NotificationSummary>();
    private readonly subscriptions: IObservableArray<Subscription> = new ObservableArray<Subscription>([]);

    private readonly serviceHooksClient: IServiceHooksRestClient;

    private readonly dataProviderDefaultData: ServiceHooksDefaultServiceData;
    private readonly tfsPageData?: ITfsPageData;

    private readonly performanceService: IVssPerformanceService;
    private readonly pageStateHistoryService: IPageStateHistoryService;
    private readonly historyParamsHandled: IObservableValue<boolean> = new ObservableValue<boolean>(false);

    private readonly contextDataLoaded: IObservableValue<boolean> = new ObservableValue<boolean>(false);
    private readonly errorMessage: IObservableValue<string | undefined> = new ObservableValue<string | undefined>(undefined);

    private readonly selectedSubscription: IObservableValue<Subscription | undefined> = new ObservableValue<Subscription | undefined>(undefined);
    private readonly selectedNotification: IObservableValue<Notification | undefined> = new ObservableValue<Notification | undefined>(undefined);

    private readonly editSubscriptionPartial: IObservableValue<Partial<Subscription>> = new ObservableValue({});
    private readonly displayPanel: IObservableValue<boolean> = new ObservableValue(false);

    private deleteTargets: Subscription[] = [];
    private readonly displayDeleteDialog: IObservableValue<boolean> = new ObservableValue(false);

    private readonly activePromises: ICancelablePromise<any>[] = [];

    constructor(props: IVssComponentProperties, context: IVssComponentContext) {
        super(props, context);
        this.serviceHooksClient = getServiceHooksClient(context.pageContext);

        const contributionService = context.pageContext.getService<IVssContributionService>("IVssContributionService");
        this.dataProviderDefaultData = contributionService.getData<ServiceHooksDefaultServiceData>(
            "ms.vss-servicehooks-web.manageServiceHooks-project-data-provider-new"
        )!;

        this.performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        this.pageStateHistoryService = this.context.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");

        const tfsPageService = context.pageContext.getService<ITfsPageService>("ITfsPageService");
        this.tfsPageData = tfsPageService.getData();

        const canView = this.dataProviderDefaultData && this.dataProviderDefaultData.userPermissions[Permissions.ViewSubscriptions];
        if (!canView) {
            this.errorMessage.value = Resources.ErrorMessageNoViewPermissions;
        }
    }

    public render() {
        const dataContext: IServiceHooksDataContext = {
            dataLoaded: this.contextDataLoaded,
            consumers: this.consumers,
            consumersById: this.consumersById,
            consumerActions: this.consumerActions,
            consumerActionsById: this.consumerActionsById,
            eventTypes: this.eventTypes,
            eventTypesById: this.eventTypesById
        };

        return (
            <ServiceHooksDataContext.Provider value={dataContext}>
                <Page className="service-hooks-admin flex-grow">
                    <Observer
                        historyParamsHandled={this.historyParamsHandled}
                        selectedNotification={this.selectedNotification}
                        selectedSubscription={this.selectedSubscription}
                    >
                        {(observerProps: {
                            historyParamsHandled: boolean;
                            selectedNotification: Notification | undefined;
                            selectedSubscription: Subscription | undefined;
                        }) => {
                            if (!observerProps.historyParamsHandled) {
                                return (
                                    <div className="flex-row flex-grow flex-center justify-center">
                                        <Spinner label={Resources.Loading} size={SpinnerSize.large} />
                                    </div>
                                );
                            }
                            if (observerProps.selectedSubscription) {
                                if (observerProps.selectedNotification) {
                                    return (
                                        <NotificationDetailView
                                            onBackClick={() => {
                                                this.selectedNotification.value = undefined;
                                            }}
                                            onGetNotificationDetails={this.queryNotification}
                                            notificationId={observerProps.selectedNotification.id}
                                            subscription={observerProps.selectedSubscription}
                                        />
                                    );
                                } else {
                                    return (
                                        <ServiceHookDetailView
                                            onBackClick={() => {
                                                this.selectedSubscription.value = undefined;
                                            }}
                                            onEditSubscription={this.openCreateSubscriptionPanel}
                                            onEnableDiagnostics={this.enableSubscriptionDiagnostics}
                                            onHistoryRowActivated={(notification: Notification) => {
                                                this.selectedNotification.value = notification;
                                            }}
                                            onDeleteSubscription={this.beginDeleteSubscription}
                                            onQueryDiagnostics={this.querySubscriptionDiagnostics}
                                            onQueryNotifications={this.queryNotifications}
                                            onUpdateSubscriptionStatus={this.updateSubscriptionStatus}
                                            subscription={observerProps.selectedSubscription}
                                            userPermissions={this.dataProviderDefaultData ? this.dataProviderDefaultData.userPermissions : []}
                                            zeroDataImagePath={getAssetLocation(
                                                this.context.pageContext,
                                                "ms.vss-servicehooks-web/admin-content/Content/zerodata.png"
                                            )}
                                        />
                                    );
                                }
                            } else {
                                return (
                                    <ServiceHooksView
                                        errorMessage={this.errorMessage}
                                        notificationSummaries={this.notificationSummaries}
                                        onCreateEditSubscription={this.openCreateSubscriptionPanel}
                                        onDeleteSubscriptions={this.beginDeleteSubscriptions}
                                        onRefresh={this.refreshData}
                                        onSubscriptionActivated={subscription => {
                                            this.selectedSubscription.value = subscription;
                                        }}
                                        onUpdateSubscriptionStatus={this.updateSubscriptionStatus}
                                        subscriptions={this.subscriptions}
                                        userPermissions={this.dataProviderDefaultData ? this.dataProviderDefaultData.userPermissions : []}
                                        zeroDataImagePath={getAssetLocation(
                                            this.context.pageContext,
                                            "ms.vss-servicehooks-web/admin-content/Content/zerodata.png"
                                        )}
                                    />
                                );
                            }
                        }}
                    </Observer>
                </Page>

                <Observer subscriptionPartial={this.editSubscriptionPartial}>
                    {(observerProps: { subscriptionPartial: Partial<Subscription> }) => (
                        <ConditionalChildren renderChildren={this.displayPanel}>
                            <ConditionalChildren renderChildren={this.contextDataLoaded}>
                                <SubscriptionCreationPanel
                                    onComplete={observerProps.subscriptionPartial.id ? this.updateSubscription : this.createSubscription}
                                    onDismiss={this.onPanelDismiss}
                                    onQueryForInputValues={this.getInputValues}
                                    onQueryTestNotification={this.queryNotification}
                                    onTest={this.testSubscription}
                                    subscriptionPartial={observerProps.subscriptionPartial}
                                />
                            </ConditionalChildren>
                        </ConditionalChildren>
                    )}
                </Observer>

                <Observer renderChildren={this.displayDeleteDialog}>
                    {(observerProps: { renderChildren: boolean }) =>
                        observerProps.renderChildren ? (
                            <Dialog
                                titleProps={{ size: TitleSize.Large, text: Resources.DialogDeleteTitle }}
                                footerButtonProps={[
                                    {
                                        id: "cancel",
                                        text: Resources.ButtonCancelText,
                                        onClick: () => (this.displayDeleteDialog.value = false)
                                    },
                                    {
                                        id: "confirm",
                                        text: Resources.ButtonConfirmText,
                                        onClick: () => {
                                            this.deleteTargets.forEach(sub => this.executeDeleteSubscription(sub));
                                            this.displayDeleteDialog.value = false;
                                        },
                                        primary: true
                                    }
                                ]}
                                lightDismiss={true}
                                onDismiss={() => (this.displayDeleteDialog.value = false)}
                            >
                                {format(
                                    this.deleteTargets.length > 1 ? Resources.DialogDeleteManyBody : Resources.DialogDeleteOneBody,
                                    this.deleteTargets.length
                                )}
                            </Dialog>
                        ) : null
                    }
                </Observer>
            </ServiceHooksDataContext.Provider>
        );
    }

    public componentDidMount() {
        // In the event we fail to load data from the data provider, don't let everything explode
        if (!this.dataProviderDefaultData) {
            this.errorMessage.value = Resources.ErrorMessageDataProviderFailed;
            this.historyParamsHandled.value = true;
            this.performanceService.markTimeToInteractive("serviceHooks", "ServiceHooks.AdminHub-New");
            return;
        }

        // If we can't view data, don't query for data
        // Need to mark tti in this and the else block
        if (this.dataProviderDefaultData.userPermissions[Permissions.ViewSubscriptions]) {
            this.refreshData().then(
                () => {
                    this.performanceService.markTimeToInteractive("serviceHooks", "ServiceHooks.AdminHub-New");

                    const currentHistoryState = this.pageStateHistoryService.getProjectedState();

                    this.pageStateHistoryService.registerObservable(
                        this.selectedSubscription,
                        "subscription",
                        HistoryBehavior.replace,
                        subscription => {
                            return subscription ? subscription.id : "";
                        },
                        navValue => {
                            return this.subscriptions.value.find(x => x.id === navValue);
                        }
                    );

                    this.pageStateHistoryService.registerObservable(
                        this.selectedNotification,
                        "notification",
                        HistoryBehavior.replace,
                        notification => {
                            return notification ? notification.id.toString() : "";
                        },
                        navValue => {
                            // We handle the delayed lookup of this later; therefore, we never need to successfully parse
                            // we literally only want to track as a nav parameter
                            return undefined;
                        }
                    );

                    if (this.selectedSubscription.value && currentHistoryState["notification"]) {
                        this.queryNotification(this.selectedSubscription.value.id, parseInt(currentHistoryState["notification"])).then(
                            notification => {
                                this.selectedNotification.value = notification;
                                this.historyParamsHandled.value = true;
                            }
                        );
                    } else {
                        this.historyParamsHandled.value = true;
                    }
                },
                () => {
                    this.historyParamsHandled.value = true;
                }
            );
        } else {
            this.performanceService.markTimeToInteractive("serviceHooks", "ServiceHooks.AdminHub-New");
            this.historyParamsHandled.value = true;
        }
    }

    public componentWillUnmount() {
        this.activePromises.forEach(x => x.cancel());
    }

    private addCancelablePromise<T>(promise: Promise<T>) {
        const cancelablePromise = makeCancelable(promise);

        const removeHandler = (value: any) => {
            this.removeActivePromise(cancelablePromise);
            return value;
        };

        promise.then(removeHandler, removeHandler);

        this.activePromises.push(cancelablePromise);
        return cancelablePromise.promise;
    }

    private removeActivePromise(cancelablePromise: ICancelablePromise<any>) {
        const index = this.activePromises.indexOf(cancelablePromise);

        if (index >= 0) {
            this.activePromises.splice(index, 1);
        }
    }

    private refreshData = () => {
        this.contextDataLoaded.value = false;

        const publishersQuery: PublishersQuery = {
            publisherIds: [],
            publisherInputs: this.tfsPageData && this.tfsPageData.project ? { projectId: this.tfsPageData.project.id } : {},
            results: []
        };

        Object.keys(this.dataProviderDefaultData.publisherServiceInstanceTypes).forEach(key => {
            publishersQuery.publisherIds.push(key);
        });

        this.performanceService.startScenario("ServiceHooks.AdminHub.fetchData", false);

        // Reset this since we're pushing below
        this.eventTypes.splice(0, this.eventTypes.length);
        const processedEventTypes: { [key: string]: boolean } = {};
        const publisherPromises = Object.keys(this.dataProviderDefaultData.publisherServiceInstanceTypes).map(key => {
            const publisherId = this.dataProviderDefaultData.publisherServiceInstanceTypes[key];
            const client = getServiceHooksClient(this.context.pageContext, { serviceInstanceType: publisherId.toLowerCase() });

            return this.addCancelablePromise(
                client.queryPublishers(publishersQuery).then(queryResults => {
                    // Emulating a set locally so we don't clear out other dictionaries

                    for (const publisher of queryResults.results) {
                        for (const eventType of publisher.supportedEvents) {
                            if (!processedEventTypes[eventType.id]) {
                                processedEventTypes[eventType.id] = true;

                                this.eventTypes.push(eventType);
                                this.eventTypesById[eventType.id] = eventType;
                                this.eventTypeIdsToPublisherTypeIds[eventType.id] = publisherId;
                            }
                        }
                    }

                    this.eventTypes.sort((a: EventTypeDescriptor, b: EventTypeDescriptor) => {
                        return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
                    });
                })
            );
        });

        const consumersPromise = this.addCancelablePromise(
            this.serviceHooksClient.listConsumers().then(consumers => {
                // Delay processing until all promises resolve, as we need some data from the publishers promise as well
                return consumers.sort((a: Consumer, b: Consumer) => {
                    return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
                });
            })
        );

        const subscriptionsPromises = Object.keys(this.dataProviderDefaultData.publisherServiceInstanceTypes).map(key => {
            const publisherId = this.dataProviderDefaultData.publisherServiceInstanceTypes[key];
            const client = publisherId
                ? getServiceHooksClient(this.context.pageContext, {
                    serviceInstanceType: publisherId.toLowerCase()
                })
                : this.serviceHooksClient;            
            return this.addCancelablePromise(
                client.createSubscriptionsQuery({
                        publisherId: key,
                        publisherInputFilters: [
                            {
                                conditions: [
                                    { inputId: "projectId", inputValue: this.tfsPageData!.project!.id, operator: InputFilterOperator.Equals }
                                ]
                            }
                        ]
                    } as SubscriptionsQuery)
                    .then(queryResult => {
                        const subscriptions = queryResult.results;

                        // Query back
                        const minDate = new Date();
                        minDate.setDate(minDate.getDay() - 7);

                        const query: NotificationsQuery = {
                            minCreatedDate: minDate,
                            subscriptionIds: [...subscriptions.map(x => x.id)]
                        } as NotificationsQuery;

                        this.addCancelablePromise(
                            this.serviceHooksClient.queryNotifications(query).then(result => {
                                result.summary.map(summary => {
                                    const existingSummary = this.notificationSummaries.get(summary.subscriptionId);
                                    if (existingSummary) {
                                        // Copy over values since .add doesn't update the value if it already exists on the object
                                        // A table re-render will already be prompted by this re-fetch so we don't need the ObservableObject to nofity;
                                        // A better solution would involve some sort of "addOrReplace" method, which is being added with this PR, but compat reasons
                                        // mean that we need to bridge the gap for now
                                        // TODO: Remove in M156
                                        Object.assign(existingSummary, summary);
                                    } else {
                                        this.notificationSummaries.add(summary.subscriptionId, summary);
                                    }
                                });
                            })
                        );

                        // Fill in gaps with blanks
                        subscriptions.forEach(subscription => {
                            if (!this.notificationSummaries.get(subscription.id)) {
                                this.notificationSummaries.add(subscription.id, { subscriptionId: subscription.id, results: [] });
                            }
                        });

                        return subscriptions;
                    })
            );
        });

        const subscriptionsPromise = Promise.all([...subscriptionsPromises]).then(results => {
            const flattenedSubscriptions = ([] as Subscription[])
                .concat(...results)
                .sort((a, b) => b.createdDate.getTime() - a.createdDate.getTime());
            this.subscriptions.value = flattenedSubscriptions;
            return flattenedSubscriptions;
        });

        return Promise.all([publisherPromises, consumersPromise, subscriptionsPromise])
            .then(([x, consumers, y]) => {
                // Process here because we needed to get data from publishers resolved first
                this.consumers.splice(0, this.consumers.length); // Clear out in the event of manual refresh
                consumers
                    .filter(consumer =>
                        consumer.actions
                            .map(action => action.supportedEventTypes)
                            .reduce((prev, curr) => prev.concat(curr))
                            .some(eventType => this.eventTypesById[eventType] !== undefined || eventType === "*")
                    )
                    .forEach(consumer => {
                        this.consumers.push(consumer);
                        this.consumersById[consumer.id] = consumer;

                        consumer.actions.forEach(action => {
                            this.consumerActions.push(action);
                            this.consumerActionsById[consumer.id + action.id] = action;
                        });
                    });

                this.performanceService.endScenario("ServiceHooks.AdminHub", "ServiceHooks.AdminHub.fetchData", false);
                this.contextDataLoaded.value = true;

                return [x, consumers, y];
            })
            .catch(reason => {
                this.errorMessage.value = reason.message as string;
            });
    };

    private createSubscription = (subscription: Subscription) => {
        if (this.consumerActionsById[subscription.consumerId + subscription.consumerActionId].allowResourceVersionOverride &&
            subscription.resourceVersion === undefined)
        {
            subscription.resourceVersion = "1.0";
        }

        if (this.tfsPageData && this.tfsPageData.project) {
            subscription.publisherInputs["projectId"] = this.tfsPageData.project.id;
        }

        const client = subscription.eventType
        ? getServiceHooksClient(this.context.pageContext, {
              serviceInstanceType: this.eventTypeIdsToPublisherTypeIds[subscription.eventType].toLowerCase()
          })
        : this.serviceHooksClient;

        return this.addCancelablePromise(
            client.createSubscription(subscription).then(fullSubscription => {
                this.subscriptions.push(fullSubscription);
                this.notificationSummaries.add(fullSubscription.id, { subscriptionId: fullSubscription.id, results: [] });
                this.displayPanel.value = false;
                return fullSubscription;
            })
        );
    };

    private updateSubscription = (subscription: Subscription) => {
        const client = subscription.eventType
        ? getServiceHooksClient(this.context.pageContext, {
              serviceInstanceType: this.eventTypeIdsToPublisherTypeIds[subscription.eventType].toLowerCase()
          })
        : this.serviceHooksClient;

        return this.addCancelablePromise(
            client.replaceSubscription(subscription).then(updatedSubscription => {
                const oldIndex = this.subscriptions.value.findIndex(x => x.id === updatedSubscription.id);
                this.subscriptions.change(oldIndex, updatedSubscription);

                if (this.selectedSubscription.value && this.selectedSubscription.value.id === subscription.id) {
                    this.selectedSubscription.value = subscription;
                }

                this.displayPanel.value = false;

                return updatedSubscription;
            })
        );
    };

    private updateSubscriptionStatus = (status: SubscriptionStatus, subscription: Subscription) => {
        const newSubscription: Subscription = { ...subscription, status: status };
        return this.updateSubscription(newSubscription);
    };

    private testSubscription = (subscription: Subscription): Promise<Notification> => {
        const testNotification = {
            subscriptionId: subscription.id,
            details: {
                consumerActionId: subscription.consumerActionId,
                consumerId: subscription.consumerId,
                consumerInputs: subscription.consumerInputs,
                eventType: subscription.eventType,
                publisherId: subscription.publisherId,
                publisherInputs: subscription.publisherInputs,
                event: {
                    resourceVersion: subscription.resourceVersion
                }
            }
        } as Notification;

        const client = subscription.eventType
            ? getServiceHooksClient(this.context.pageContext, {
                  serviceInstanceType: this.eventTypeIdsToPublisherTypeIds[subscription.eventType].toLowerCase()
              })
            : this.serviceHooksClient;

        return this.addCancelablePromise(
            client.createTestNotification(testNotification).then(notification => {
                return notification;
            })
        );
    };

    private queryNotification = (subscriptionId: string, notificationId: number): Promise<Notification> => {
        return this.addCancelablePromise(
            this.serviceHooksClient.getNotification(subscriptionId, notificationId).then(notification => {
                return notification;
            })
        );
    };

    private beginDeleteSubscription = (subscription: Subscription) => {
        this.beginDeleteSubscriptions([subscription]);
    };

    private beginDeleteSubscriptions = (subscriptions: Subscription[]) => {
        this.displayDeleteDialog.value = true;
        this.deleteTargets = subscriptions;
    };

    private executeDeleteSubscription = (subscription: Subscription) => {
        const client = subscription.eventType
        ? getServiceHooksClient(this.context.pageContext, {
              serviceInstanceType: this.eventTypeIdsToPublisherTypeIds[subscription.eventType].toLowerCase()
          })
        : this.serviceHooksClient;
        return this.addCancelablePromise(
            client.deleteSubscription(subscription.id).then(() => {
                if (this.selectedSubscription.value && this.selectedSubscription.value.id === subscription.id) {
                    this.selectedSubscription.value = undefined;
                }
                this.subscriptions.removeAll(item => item.id === subscription.id);
            })
        );
    };

    private getInputValues = (subscriptionPartial: Partial<Subscription>, inputValues: InputValues[], scope?: SubscriptionInputScope) => {
        this.performanceService.startScenario("ServiceHooks.AdminHub.fetchInputDetails", this.contextDataLoaded.value);

        // Stamp this just in case it's unpopulated
        if (this.tfsPageData && this.tfsPageData.project) {
            subscriptionPartial.publisherInputs!["projectId"] = this.tfsPageData.project.id;
        }

        const inputValuesQuery: SubscriptionInputValuesQuery = {
            inputValues: inputValues,
            subscription: subscriptionPartial as Subscription,
            scope: scope || SubscriptionInputScope.Publisher
        };

        const client =
            scope === SubscriptionInputScope.Publisher && subscriptionPartial.eventType
                ? getServiceHooksClient(this.context.pageContext, {
                      serviceInstanceType: this.eventTypeIdsToPublisherTypeIds[subscriptionPartial.eventType].toLowerCase()
                  })
                : this.serviceHooksClient;

        return this.addCancelablePromise(
            client.postInputValuesQuery(inputValuesQuery).then(retVal => {
                this.performanceService.endScenario("ServiceHooks.AdminHub", "ServiceHooks.AdminHub.fetchInputDetails", this.contextDataLoaded.value);
                return retVal.inputValues;
            })
        );
    };

    private queryNotifications = (subscription: Subscription, daysBack: number) => {
        this.performanceService.startScenario("ServiceHooks.AdminHub.queryNotifications", this.contextDataLoaded.value);

        const dateInPast = new Date();
        dateInPast.setDate(dateInPast.getDate() - daysBack);

        const notificationQuery: NotificationsQuery = {
            minCreatedDate: dateInPast,
            subscriptionIds: [subscription.id]
        } as NotificationsQuery;
        
        const client = subscription.eventType
            ? getServiceHooksClient(this.context.pageContext, {
                  serviceInstanceType: this.eventTypeIdsToPublisherTypeIds[subscription.eventType].toLowerCase()
              })
            : this.serviceHooksClient;

        return this.addCancelablePromise(
            client.queryNotifications(notificationQuery).then(result => {
                this.performanceService.endScenario(
                    "ServiceHooks.AdminHub",
                    "ServiceHooks.AdminHub.queryNotifications",
                    this.contextDataLoaded.value
                );

                return result.results;
            })
        );
    };

    private querySubscriptionDiagnostics = (subscription: Subscription) => {
        const client = subscription.eventType
            ? getServiceHooksClient(this.context.pageContext, {
                  serviceInstanceType: this.eventTypeIdsToPublisherTypeIds[subscription.eventType].toLowerCase()
              })
            : this.serviceHooksClient;

        return this.addCancelablePromise(client.getSubscriptionDiagnostics(subscription.id));
    };

    private enableSubscriptionDiagnostics = (subscription: Subscription) => {
        const client = subscription.eventType
            ? getServiceHooksClient(this.context.pageContext, {
                  serviceInstanceType: this.eventTypeIdsToPublisherTypeIds[subscription.eventType].toLowerCase()
              })
            : this.serviceHooksClient;

        const params = { deliveryResults: { enabled: true } } as UpdateSubscripitonDiagnosticsParameters;
        return this.addCancelablePromise(client.updateSubscriptionDiagnostics(params, subscription.id));
    };

    private onPanelDismiss = () => {
        this.displayPanel.value = false;
    };

    private openCreateSubscriptionPanel = (newSubscriptionPartial: Partial<Subscription>) => {
        this.editSubscriptionPartial.value = newSubscriptionPartial;
        this.displayPanel.value = true;
    };
}

Components.add("servicehooks-tab-content", ServiceHooksTabContent);
